import React, { useState } from "react";
import { Plus, X, ChevronDown } from "lucide-react";
import { K } from "../theme";
import { Sl } from "../components";

// Monday-first for display; the number is the JS/Postgres weekday (0 = Sunday).
export const DAYS = [[1, "Monday", "M"], [2, "Tuesday", "T"], [3, "Wednesday", "W"], [4, "Thursday", "T"], [5, "Friday", "F"], [6, "Saturday", "S"], [0, "Sunday", "S"]];
export const TYPE = {
  working: { l: "Working day", c: K.g },
  off: { l: "Week off", c: K.r },
  half_day: { l: "Half day", c: K.pu },
  wfh: { l: "Work from home", c: K.bl },
};
const TYPE_OPTS = Object.entries(TYPE).map(([v, t]) => ({ v, l: t.l }));
const WEEKS = [[1, "1st"], [2, "2nd"], [3, "3rd"], [4, "4th"], [5, "5th"]];
const inp = "px-[8px] py-[6px] bg-bg2 border border-b0 rounded-[8px] text-[13px] text-t0 outline-none";

// Default for a new shift: Monday–Friday working, Saturday & Sunday off.
export const DEFAULT_PATTERN = () => [1, 2, 3, 4, 5].map((w) => ({ weekday: w, weeks: "", day_type: "working" })).concat([0, 6].map((w) => ({ weekday: w, weeks: "", day_type: "off" })));

const PRESETS = [
  { l: "Mon–Fri · Sat & Sun off", rules: DEFAULT_PATTERN },
  { l: "Mon–Sat · Sunday off", rules: () => [1, 2, 3, 4, 5, 6].map((w) => ({ weekday: w, weeks: "", day_type: "working" })).concat([{ weekday: 0, weeks: "", day_type: "off" }]) },
  { l: "Mon–Sat · 2nd & 4th Sat off", rules: () => [1, 2, 3, 4, 5].map((w) => ({ weekday: w, weeks: "", day_type: "working" })).concat([{ weekday: 6, weeks: "2,4", day_type: "off" }, { weekday: 6, weeks: "1,3,5", day_type: "working" }, { weekday: 0, weeks: "", day_type: "off" }]) },
  { l: "Mon–Fri · Saturday half day", rules: () => [1, 2, 3, 4, 5].map((w) => ({ weekday: w, weeks: "", day_type: "working" })).concat([{ weekday: 6, weeks: "", day_type: "half_day" }, { weekday: 0, weeks: "", day_type: "off" }]) },
];

const blank = (weekday, weeks, day_type) => ({ weekday, weeks, day_type, start_time: "", end_time: "", late_threshold_minutes: "", min_hours: "", half_day_hours: "" });
const clean = (v) => (v == null ? "" : v);

// A row of seven small pills (M T W T F S S) coloured by day type — the whole week at a glance.
export const WeekPills = ({ rules = [] }) => (
  <div className="flex gap-[3px]">
    {DAYS.map(([wd, name, ch]) => {
      const rs = rules.filter((r) => r.weekday === wd);
      const main = rs.find((r) => !r.weeks) || rs[0];
      const t = main ? TYPE[main.day_type] : null;
      const tip = rs.length ? rs.map((r) => `${TYPE[r.day_type].l}${r.start_time ? ` ${r.start_time}–${r.end_time}` : ""}${r.weeks ? ` (weeks ${r.weeks})` : ""}`).join(" + ") : "Not set — old Sunday/Saturday rule";
      return (
        <span key={wd} title={`${name}: ${tip}`} className="w-[22px] h-[22px] rounded-md flex items-center justify-center text-[11px] font-extrabold"
          style={{ background: t ? `${t.c}22` : K.bg3, color: t ? t.c : K.t3, border: `1px solid ${t ? `${t.c}55` : K.b0}`, position: "relative" }}>
          {ch}{rs.length > 1 && <i style={{ position: "absolute", top: -2, right: -2, width: 6, height: 6, borderRadius: 3, background: K.a }} />}
        </span>
      );
    })}
  </div>
);

// Weekly pattern of a shift: for every weekday, working / week off / half day / work from home,
// the shift's hours by default, and optional shorter (or different) hours for individual days.
export const WeekPatternEditor = ({ rules = [], onChange, shiftStart, shiftEnd, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [more, setMore] = useState({});
  const rs = rules.map((r) => ({ ...r, weeks: clean(r.weeks), start_time: clean(r.start_time), end_time: clean(r.end_time),
    late_threshold_minutes: clean(r.late_threshold_minutes), min_hours: clean(r.min_hours), half_day_hours: clean(r.half_day_hours) }));
  const set = (list) => onChange(list);
  const patch = (i, p) => set(rs.map((r, k) => (k === i ? { ...r, ...p } : r)));
  const drop = (i) => set(rs.filter((_, k) => k !== i));
  const toggleWeek = (i, n) => set(rs.map((r, k) => {
    if (k !== i) return r;
    const s = new Set(String(r.weeks).split(",").filter(Boolean).map(Number));
    s.has(n) ? s.delete(n) : s.add(n);
    return { ...r, weeks: [...s].sort().join(",") };
  }));
  const shiftHours = shiftStart && shiftEnd ? `${shiftStart}–${shiftEnd}` : "shift hours";

  return (
    <div className="mt-2 border border-b0 rounded-[10px] bg-bg1">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-3 py-2 bg-transparent border-0 cursor-pointer text-left">
        <span className="text-[13px] font-extrabold text-t0">Weekly pattern</span>
        <WeekPills rules={rs} />
        <span className="text-[12px] text-t3 hidden sm:inline">Which days work, which are off, and the hours for each</span>
        <ChevronDown size={14} className="ml-auto text-t3" style={{ transform: open ? "rotate(180deg)" : undefined }} />
      </button>

      {open && (
        <div className="px-3 pb-3">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-t3">Quick start</span>
            {PRESETS.map((p) => (
              <button type="button" key={p.l} onClick={() => set(p.rules())} className="px-[10px] py-[4px] rounded-full text-[12px] font-bold cursor-pointer border border-b0 bg-s text-t2 hover:text-p hover:border-p">{p.l}</button>
            ))}
          </div>
          <div className="text-[12px] text-t3 mb-2">Every working day uses the shift hours ({shiftHours}) unless you give that day its own hours — e.g. a shorter Friday or Saturday.</div>

          <div className="border border-b0 rounded-lg overflow-hidden">
            {DAYS.map(([wd, name], di) => {
              const list = rs.map((r, i) => ({ r, i })).filter((x) => x.r.weekday === wd);
              const hasException = list.some((x) => x.r.weeks);
              return (
                <div key={wd} className={`px-3 py-2 ${di ? "border-t border-b0" : ""}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="w-[88px] pt-[7px] text-[13.5px] font-extrabold text-t0">{name}</div>
                    <div className="flex-1 min-w-[280px] space-y-2">
                      {list.length === 0 && (
                        <div className="flex items-center gap-3">
                          <div className="w-[160px]"><Sl options={[{ v: "", l: "Not set" }, ...TYPE_OPTS]} value="" onChange={(v) => v && set([...rs, blank(wd, "", v)])} /></div>
                          <span className="text-[12px] text-t3">Falls back to the old Sunday / Saturday rule</span>
                        </div>
                      )}
                      {list.map(({ r, i }) => {
                        const k = `${wd}-${i}`; const custom = !!(r.start_time || r.end_time);
                        return (
                          <div key={i} className="rounded-lg border border-b0 bg-bg2/60 p-2">
                            <div className="flex flex-wrap items-center gap-2">
                              {hasException && <span className="text-[11px] font-extrabold uppercase text-t3 w-[74px]">{r.weeks ? "These weeks" : "Other weeks"}</span>}
                              <div className="w-[160px]"><Sl options={TYPE_OPTS} value={r.day_type} onChange={(v) => patch(i, { day_type: v })} /></div>
                              {r.day_type !== "off" && (custom ? <>
                                <input type="time" className={inp + " w-[108px]"} value={r.start_time} onChange={(e) => patch(i, { start_time: e.target.value })} />
                                <span className="text-t3 text-[12px]">to</span>
                                <input type="time" className={inp + " w-[108px]"} value={r.end_time} onChange={(e) => patch(i, { end_time: e.target.value })} />
                                <button type="button" onClick={() => patch(i, { start_time: "", end_time: "" })} className="text-[12px] font-bold text-t3 hover:text-p bg-transparent border-0 cursor-pointer">Use shift hours</button>
                              </> : <>
                                <span className="text-[12.5px] text-t2">{shiftHours}</span>
                                <button type="button" onClick={() => patch(i, { start_time: shiftStart || "09:00", end_time: shiftEnd || "18:00" })} className="text-[12px] font-bold text-p bg-transparent border-0 cursor-pointer">Custom hours</button>
                              </>)}
                              {r.day_type !== "off" && <button type="button" onClick={() => setMore((m) => ({ ...m, [k]: !m[k] }))} className="text-[12px] font-bold text-p bg-transparent border-0 cursor-pointer flex items-center gap-[2px]">Limits<ChevronDown size={12} style={{ transform: more[k] ? "rotate(180deg)" : undefined }} /></button>}
                              {(r.weeks || hasException) && (
                                <div className="flex items-center gap-[4px] ml-auto">
                                  {r.weeks !== undefined && r.weeks !== "" && <>
                                    <span className="text-[11px] text-t3 font-bold uppercase mr-1">Weeks</span>
                                    {WEEKS.map(([n, l]) => {
                                      const on = String(r.weeks).split(",").includes(String(n));
                                      return <button type="button" key={n} onClick={() => toggleWeek(i, n)} className={`px-[8px] py-[3px] rounded-full text-[11.5px] font-bold cursor-pointer border ${on ? "bg-p text-white border-p" : "bg-s text-t2 border-b0"}`}>{l}</button>;
                                    })}
                                  </>}
                                </div>
                              )}
                              <button type="button" onClick={() => drop(i)} title="Remove this rule" className="text-t3 hover:text-r bg-transparent border-0 cursor-pointer p-1 ml-auto"><X size={14} /></button>
                            </div>
                            {more[k] && r.day_type !== "off" && (
                              <div className="grid grid-cols-3 gap-2 mt-2 max-w-[520px]">
                                <label className="text-[11px] text-t3 font-bold">Late after (min)<input type="number" min="0" className={inp + " mt-1 w-full"} value={r.late_threshold_minutes} onChange={(e) => patch(i, { late_threshold_minutes: e.target.value })} placeholder="shift value" /></label>
                                <label className="text-[11px] text-t3 font-bold">Min hours<input type="number" min="0" step="0.5" className={inp + " mt-1 w-full"} value={r.min_hours} onChange={(e) => patch(i, { min_hours: e.target.value })} placeholder="shift value" /></label>
                                <label className="text-[11px] text-t3 font-bold">Half-day hours<input type="number" min="0" step="0.5" className={inp + " mt-1 w-full"} value={r.half_day_hours} onChange={(e) => patch(i, { half_day_hours: e.target.value })} placeholder="shift value" /></label>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {list.length > 0 && !hasException && (
                        <button type="button" onClick={() => set([...rs, blank(wd, "2,4", list[0].r.day_type === "off" ? "working" : "off")])} className="text-[12px] font-bold text-p bg-transparent border-0 cursor-pointer flex items-center gap-1 p-0"><Plus size={12} />Different on certain weeks (e.g. 2nd &amp; 4th {name})</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
